import { ExportPresetResource } from "@/types/resources";
import { FC, FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import { ExportGroup, Footer } from ".";
import Button from "../Button";
import FormField from "../Forms/FormField";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { scrollBarStyles } from "@/utils/theme";
import ColumnsSelection from "./ColumnsSelection";

const Body = styled.div`
  min-height: 613px;
  text-align: left;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const Field = styled.div`
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(4, 25%);
`;

const PresetColumns = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

interface FormDataT {
  name: string;
  group: string;
  exportables: string[];
}

interface Props {
  editablePreset: ExportPresetResource | null;
  exportables?: string[];
  columns: string[];
  exportGroup: ExportGroup;
  onBack: () => void;
  afterSubmitCallback?: (preset: ExportPresetResource) => void;
}

const PresetFormScreen: FC<Props> = ({
  editablePreset,
  exportables,
  exportGroup,
  afterSubmitCallback,
  columns,
  onBack,
}) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    watch,
    setValue,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editablePreset?.name ?? "",
      group: editablePreset?.group ?? exportGroup,
      exportables: editablePreset?.exportables ?? exportables,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const preset = await (editablePreset
          ? api.put<FormDataT, ExportPresetResource>(
              `/v1/export-presets/${editablePreset.id}`,
              data
            )
          : api.post<FormDataT, ExportPresetResource>(
              `/v1/export-presets`,
              data
            ));

        await queryClient.invalidateQueries([
          "export_presets",
          { group: exportGroup },
        ]);

        alerts.success(
          editablePreset
            ? "Preset has been successfully updated"
            : "Preset has been successfully created",
          preset.name
        );

        afterSubmitCallback?.(preset);
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  const formExportables = watch("exportables");

  const toggleColumn = (col: string) => {
    if (formExportables.indexOf(col) === -1) {
      return setValue("exportables", [...formExportables, col], {
        shouldDirty: true,
      });
    }

    setValue(
      "exportables",
      formExportables.filter((c) => c !== col, { shouldDirty: true })
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <Body>
        <Field>
          <FormField<FormDataT>
            control={control}
            name="name"
            label="Preset Name"
            subLabel="(Up to 16 characters)"
            placeholder="Add name"
          />
        </Field>
        <PresetColumns>
          <ColumnsSelection
            columns={columns}
            exportables={formExportables}
            onColumnsReorder={(cols) =>
              setValue("exportables", cols, { shouldDirty: true })
            }
            toggleColumn={toggleColumn}
          />
        </PresetColumns>
      </Body>
      <Footer>
        <Button type="button" color="secondary" onClick={onBack}>
          Back
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting || !isDirty}
        >
          {editablePreset ? "Update" : "Create"}
        </Button>
      </Footer>
    </form>
  );
};

export default PresetFormScreen;
